// Voice editor — 3.5. Mode IA (US-084 / US-085 / US-086 / US-087).
//
// Wave B3 — segmented toggles for the four account-level AI policy levers
// the audit flagged as missing from the voice editor:
//   - aiMode         (Auto / Review)
//   - showOffTarget  (visibility of the off-target pill in the inbox)
//   - offTargetMode  (Block / Suggest / Manual — the inbox + draft route
//                     branch on this server-side)
//   - autoLeadDetect (server-side auto-extraction in the ingest consumer)
//
// All four mutations route through `useUpdateLinkedinAccount` which already
// handles optimistic update + voice-query invalidation.
//
// Extracted verbatim from `VoiceEditorPage.tsx` (no behavior change).

import { type VoiceAccountSummary } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { FieldAssembly, Segmented, Switch } from '@barel/ui';

import { SectionShell } from './SectionShell';

interface ModeSectionProps {
  account: VoiceAccountSummary;
  onPatch: (settings: {
    aiMode?: 'auto' | 'review';
    showOffTarget?: boolean;
    offTargetMode?: 'block' | 'suggest' | 'manual';
    autoLeadDetect?: boolean;
  }) => Promise<void>;
}

export function ModeSection({ account, onPatch }: ModeSectionProps) {
  const t = useT('voice');
  const aiMode: 'auto' | 'review' = account.aiMode === 'auto' ? 'auto' : 'review';
  const showOffTarget = account.showOffTarget ?? true;
  const offTargetMode: 'block' | 'suggest' | 'manual' = account.offTargetMode ?? 'block';
  const autoLeadDetect = Boolean(account.autoLeadDetect);

  return (
    <SectionShell id="voice-mode" title={t('mode_section.title')}>
      <div className="flex flex-col gap-4">
        <FieldAssembly label={t('mode_section.aiMode.label')} asChild>
          <div role="group" aria-label={t('mode_section.aiMode.label')}>
            <Segmented<'auto' | 'review'>
              value={aiMode}
              onValueChange={(next) => {
                void onPatch({ aiMode: next });
              }}
              options={[
                { value: 'auto', label: t('mode_section.aiMode.auto') },
                { value: 'review', label: t('mode_section.aiMode.review') },
              ]}
              aria-label={t('mode_section.aiMode.label')}
              size="sm"
            />
          </div>
        </FieldAssembly>

        <FieldAssembly label={t('mode_section.showOffTarget.label')} asChild>
          <Switch
            aria-label={t('mode_section.showOffTarget.label')}
            checked={showOffTarget}
            onCheckedChange={(next: boolean) => {
              void onPatch({ showOffTarget: next });
            }}
            data-testid="voice-show-off-target-switch"
          />
        </FieldAssembly>

        <FieldAssembly label={t('mode_section.offTargetMode.label')} asChild>
          <div role="group" aria-label={t('mode_section.offTargetMode.label')}>
            <Segmented<'block' | 'suggest' | 'manual'>
              value={offTargetMode}
              onValueChange={(next) => {
                void onPatch({ offTargetMode: next });
              }}
              options={[
                { value: 'block', label: t('mode_section.offTargetMode.block') },
                { value: 'suggest', label: t('mode_section.offTargetMode.suggest') },
                { value: 'manual', label: t('mode_section.offTargetMode.review') },
              ]}
              aria-label={t('mode_section.offTargetMode.label')}
              size="sm"
            />
          </div>
        </FieldAssembly>

        <FieldAssembly label={t('mode_section.autoLeadDetect.label')} asChild>
          <Switch
            aria-label={t('mode_section.autoLeadDetect.label')}
            checked={autoLeadDetect}
            onCheckedChange={(next: boolean) => {
              void onPatch({ autoLeadDetect: next });
            }}
            data-testid="voice-auto-lead-detect-switch"
          />
        </FieldAssembly>
      </div>
    </SectionShell>
  );
}
